import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import type { PresentationReviewResponse } from "@/api/types";
import { usePresentation } from "@/api/presentation-review";
import { ErrorBanner } from "@/components/ErrorBanner";
import { MemoDisplay } from "@/components/presentation-review/MemoDisplay";
import { RecommendationsDisplay } from "@/components/presentation-review/RecommendationsDisplay";
import { ReviewDecisionPanel } from "@/components/presentation-review/ReviewDecisionPanel";
import { Skeleton } from "@/components/ui/skeleton";
import { formatJobNumber } from "@/lib/format";

/**
 * Build the page title from the friendliest identifier available on
 * the presentation. Preference order:
 *   1. `<client_name> — <client_company>` — the M2 launch bar.
 *   2. `Memo #00000042` — when the client identity has been
 *      anonymised (2y sweeper). `memoNumber` itself is never null
 *      (backfilled bigserial, unaffected by anonymisation), so this
 *      branch always has a value to render.
 *
 * A raw UUID prefix is NEVER exposed as visible text (issue #575).
 * The `shortId` remains available via the `title` attribute for
 * forensic hover.
 *
 * Exported for unit testing.
 */
export function buildPresentationTitle(
  presentation: Pick<
    PresentationReviewResponse,
    "clientName" | "clientCompany" | "memoNumber"
  >,
): string {
  if (presentation.clientName && presentation.clientCompany) {
    return `${presentation.clientName} — ${presentation.clientCompany}`;
  }
  return `Memo #${formatJobNumber(presentation.memoNumber)}`;
}

/**
 * `/presentations/:id/review` — the M2 admin HITL surface for
 * `solution_presentations` (issue #144). Sibling of
 * {@link ReviewBatch} for curator-draft batches.
 *
 * Layout: memo + recommendations side-by-side on wide viewports
 * (operator's laptop); stacked on narrow. The decision footer pins
 * to the bottom of the page after both panels.
 *
 * The `:id` segment is polymorphic (issue #603): the canonical form
 * is the parent memo's `memo_number` (`/presentations/00000042/review`);
 * a UUID form is accepted as a backwards-compat alias for legacy
 * bookmarks + audit-log links + email-embedded URLs. The orchestrator
 * accepts both via {@link resolvePresentationIdentifier}; the SPA
 * canonicalises the URL client-side once data lands (silent
 * `navigate(..., { replace: true })`, mirrors
 * `apps/admin-ui/src/routes/JobDetail.tsx:315-336`).
 */
export function PresentationReview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = usePresentation(id);

  // Canonicalise the URL to the friendly memo-number form once the
  // presentation loads. UUID URLs (legacy bookmarks, audit-log
  // deep-links, current email templates) resolve identically
  // downstream — the orchestrator accepts both shapes — but the
  // rendered URL flips to `/presentations/<padded-number>/review`
  // with a silent `replace` (no back-button entry). Skips the
  // no-op self-replace that React StrictMode would otherwise
  // trigger. Mirrors the M1 #100 posture on JobDetail.
  const canonicalForm = query.data
    ? formatJobNumber(query.data.memoNumber)
    : null;
  useEffect(() => {
    if (!id || !canonicalForm) return;
    if (id === canonicalForm) return;
    navigate(`/presentations/${canonicalForm}/review`, { replace: true });
  }, [id, canonicalForm, navigate]);

  if (query.isPending) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }
  if (query.isError) {
    return <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />;
  }

  const presentation = query.data;
  const deadlineText = presentation.reviewDeadlineAt
    ? new Date(presentation.reviewDeadlineAt).toLocaleString()
    : null;

  const title = buildPresentationTitle(presentation);

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold" title={presentation.shortId}>
          {title}
        </h1>
        <div className="text-sm text-muted-foreground">
          Status: <span className="font-mono">{presentation.status}</span>
          {deadlineText && <> · Deadline {deadlineText}</>}
        </div>
      </header>

      <div className="grid gap-6 md:grid-cols-2">
        <MemoDisplay memo={presentation.memo} />
        <RecommendationsDisplay
          recommendations={presentation.recommendations}
          matchValidationScore={presentation.matchValidationScore}
          matchValidationStatus={presentation.matchValidationStatus}
        />
      </div>

      <ReviewDecisionPanel presentation={presentation} />
    </div>
  );
}
